import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Popover,
  styled,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { memo, MouseEvent, useState } from 'react';
import { AppName } from '../../utils/types';
import { APP_CARDS_META } from '../../utils/consts';
import { AppCardButton } from '../../shared/styled';

const HAS_ACCESS = 'Open';
const REQUEST_ACCESS = 'Request Access';

/**
 * Render a QR code in the popover under the mobile app card
 */
const MobileQRCode = ({ buttonText }: { buttonText?: string }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [isMobile] = useState<boolean>(window.mobileCheck());
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    // use custom regex to check if browser is on mobile, otherwise fall back to breakpoint check
    if (isMobile || isSmallScreen) {
      // navigate directly to the mobile app store
      window.location.href = 'https://qrco.de/bcizPZ';
    } else {
      // show the QR code popover
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'mobile-qr-code-popover' : undefined;

  return (
    <>
      <AppCardButton
        color="primary"
        variant="contained"
        aria-describedby={id}
        onClick={handleClick}
      >
        {buttonText ? HAS_ACCESS : REQUEST_ACCESS}
      </AppCardButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        // note: only "background: transparent" works, not "backgroundColor: transparent"
        slotProps={{ paper: { sx: { background: 'transparent' } } }}
      >
        <QRCode
          src={'/mobile-app-qr-code.png'}
          alt={'download the BrainOS mobile app'}
        />
      </Popover>
    </>
  );
};

const AppCard = ({
  appName,
  hasAccess,
}: {
  appName: AppName;
  hasAccess?: boolean;
}) => {
  const { logo, name, description, destination } = APP_CARDS_META[appName];
  const buttonText = hasAccess ? HAS_ACCESS : REQUEST_ACCESS;
  const theme = useTheme();

  return (
    <StyledAppCard data-testid={`${appName}-appCard`}>
      <CardHeader
        avatar={logo}
        title={<Typography variant="h6">{name}</Typography>}
        disableTypography
        sx={{
          padding: theme.spacing(3, 3, 2, 3),
          '.MuiCardHeader-avatar': { marginRight: 1 },
        }}
      />
      <CardContent sx={{ padding: theme.spacing(0, 3) }}>
        {/* use minHeight to force all elements to be, at minimum, "2 lines of text tall" */}
        <Typography variant="body2" sx={{ minHeight: 41 }}>
          {description}
        </Typography>
      </CardContent>
      <CardActions sx={{ padding: theme.spacing(3) }}>
        {appName === 'mobile' ? (
          <MobileQRCode buttonText={buttonText} />
        ) : (
          <AppCardButton
            data-testid={`${appName}-button`}
            color="primary"
            variant="contained"
            href={destination}
            target="_blank"
          >
            {buttonText}
          </AppCardButton>
        )}
      </CardActions>
    </StyledAppCard>
  );
};

const StyledAppCard = styled(Card)(() => ({
  // IMPORTANT: Width is determined by parent MUI Grid
  minHeight: '196px',
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'nowrap',
  borderRadius: '8px',
}));

const QRCode = styled('img')(() => ({
  maxWidth: '250px',
}));

const AppCardMemo = memo(AppCard);

export default AppCardMemo;
